import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./App.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@geti/identidade-visual-react";
import Home from "./Pages/Home/Home";
import Header from "./Components/Header/Header";
import Login from "./Pages/Login/Login";
import Status from "./Pages/Status/Status";
import ProtectedRoute from "./Auth/protectedRoute";
import A11y from "./Components/A11y/A11y";
import RevokeEditions from "./Pages/RevokeEditions/RevokeEditions";
import Include from "./Pages/Include/Include";
import Tooltips from "./Components/Tooltip/Tooltip";
import Tutorial from "./Components/Tutorial/Tutorial";
import SIT from "./Components/SIT/SIT";
import Callback from "./Pages/Callback/Callback";
import History from "./Pages/History/History";

function App() {
    const [colorInverted, setColorInverted] = useState(false);
    const [fontSize, setFontSize] = useState(1);
    const [grayscale, setGrayscale] = useState(false);
    const [customCursor, setCustomCursor] = useState(false);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [isOpenModal, setIsOpenModal] = useState(true);

    const handleOutsideClick = () => {
        setIsOpenModal(true);
    };

    return (
        <BrowserRouter>
            <RouterContent
                colorInverted={colorInverted}
                setColorInverted={setColorInverted}
                fontSize={fontSize}
                setFontSize={setFontSize}
                grayscale={grayscale}
                setGrayscale={setGrayscale}
                customCursor={customCursor}
                setCustomCursor={setCustomCursor}
                mousePosition={mousePosition}
                setMousePosition={setMousePosition}
                isOpenModal={isOpenModal}
                setIsOpenModal={setIsOpenModal}
                handleOutsideClick={handleOutsideClick}
            />
        </BrowserRouter>
    );
}

function RouterContent({
    colorInverted,
    setColorInverted,
    fontSize,
    setFontSize,
    grayscale,
    setGrayscale,
    customCursor,
    setCustomCursor,
    mousePosition,
    setMousePosition,
    isOpenModal,
    setIsOpenModal,
    handleOutsideClick,
}: any) {
    const location = useLocation();
    const isViewerPath = location.pathname.includes("viewer");

    useEffect(() => {
        if (!customCursor || isViewerPath) return;
        const onMove = (e: MouseEvent) => {
            setMousePosition({ x: e.clientX, y: e.clientY });
        };
        document.addEventListener("mousemove", onMove, { passive: true });
        return () => document.removeEventListener("mousemove", onMove);
    }, [customCursor, isViewerPath, setMousePosition]);

    useEffect(() => {
        if (!customCursor || isViewerPath) {
            document.body.style.cursor = "";
            return;
        }
        document.body.style.cursor = "none";
        return () => {
            document.body.style.cursor = "";
        };
    }, [customCursor, isViewerPath]);

    useEffect(() => {
        if (isViewerPath) {
            document.documentElement.style.fontSize = "";
            return;
        }
        document.documentElement.style.fontSize = `${fontSize * 100}%`;
        return () => {
            document.documentElement.style.fontSize = "";
        };
    }, [fontSize, isViewerPath]);

    const accessibilityFilter =
        !isViewerPath && (grayscale || colorInverted)
            ? [grayscale ? "grayscale(1)" : null, colorInverted ? "invert(0.7)" : null]
                  .filter(Boolean)
                  .join(" ")
            : undefined;

    const appShellStyle: React.CSSProperties = isViewerPath
        ? {}
        : ({
              minHeight: "100vh",
              cursor: `${customCursor ? "none" : "auto"}`,
              "--cursor-pointer": `${customCursor ? "none" : "pointer"}`,
              "--cursor-not-allowed": `${customCursor ? "none" : "not-allowed"}`,
              "--cursor-default": `${customCursor ? "none" : "default"}`,
              "--cursor-text": `${customCursor ? "none" : "text"}`,
          } as React.CSSProperties);

    const mainRoutes = (
        <>
            <Header />
            <main>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/agendar" element={<ProtectedRoute Component={Status} path="/agendar" />} />
                    <Route path="/revogar-ato/:fileName" element={<ProtectedRoute Component={RevokeEditions} path="/revogar-ato/:fileName" />} />
                    <Route path="/incluir/:fileName" element={<ProtectedRoute Component={Include} path="/incluir/:fileName" />} />
                    <Route path="/callback" element={<Callback />} />
                    <Route path="/historico" element={<ProtectedRoute Component={History} path="/historico" />} />
                </Routes>
            </main>
        </>
    );

    return (
        <>
            <div style={isViewerPath ? {} : appShellStyle} onClick={handleOutsideClick}>
                <div style={accessibilityFilter ? { filter: accessibilityFilter, minHeight: '100vh' } : { minHeight: '100vh' }}>
                    {mainRoutes}
                </div>
                
                <div 
                    className={`container-acessibility ${!isViewerPath && grayscale ? 'grayscale' : ''} ${!isViewerPath && colorInverted ? 'invert-colors' : ''}`} 
                    onClick={(e) => e.stopPropagation()}
                >
                    <Tooltips placement={"left"} message="Tutorial">
                        <Tutorial breakpoint={"test"}/>
                    </Tooltips>

                    <A11y
                        setColorInverted={setColorInverted}
                        colorInverted={colorInverted}
                        setFontSize={setFontSize}
                        setGrayscale={setGrayscale}
                        grayscale={grayscale}
                        setCustomCursor={setCustomCursor}
                        isOpenModal={isOpenModal}
                        setIsOpenModal={setIsOpenModal}
                    />

                    <Tooltips placement={"left"} message="Abrir chamado no SIT">
                        <SIT/>
                    </Tooltips>
                </div>
            </div>
            
            {customCursor &&
                !isViewerPath &&
                createPortal(
                    <div
                        className="cursor"
                        style={{
                            top: mousePosition.y + "px",
                            left: mousePosition.x + "px",
                        }}
                    />,
                    document.body,
                )}
            <Toaster richColors position="top-right" />
        </>
    );
}

export default App;